import React, { Fragment } from 'react';
import cx from 'classnames';

import { constants, SiteConfigManager, useLabel } from '@atlas/core';
import { EventLiveParticipants, Icon, Label } from '@atlas/ui';

import {
  SPORT_IDS_WITH_POINTS,
  SPORT_IDS_WITH_SETS,
  SPORT_IDS_WITHOUT_PERIOD_VIEW,
} from 'common/constants';
import eventShape from 'common/PropTypes/eventShape';
import { kebabCase } from 'common/utils/kebabCase';
import EventLiveStatus from 'common/Widgets/Tournament/Tournament/Events/Event/EventLiveStatus';

const getScoreCellClassName = (isHeading = false, isGeneral = false, icon = '') =>
  cx([
    'score',
    isHeading && 'hr',
    isGeneral && (isHeading ? 'general' : 'ct'),
    icon && `gameStatIcon-${kebabCase(icon)}`,
  ]);

const getDisplayScore = score => {
  const numScore = parseInt(score);

  return Number.isNaN(numScore) ? score : numScore;
};

const { SPORT_SERVICE } = constants;

const Scoreboard = ({ event }) => {
  const {
    info: {
      score,
      score: { awayScore, homeScore } = {},
      stats: { away, home, periodInfo } = {},
    } = {},
    participants,
    sportId,
    sportService,
  } = event;

  const shownEventId = SiteConfigManager.getConfig('config.config.sportsbook.showEventId');

  const renderPeriodView = () => {
    if (SPORT_IDS_WITHOUT_PERIOD_VIEW.includes(sportId)) {
      return null;
    }

    return Object.values(periodInfo)?.map(
      ({ awayScore, homeScore, periodDescription, shortPeriodDescription }) => {
        const title =
          periodDescription === 'Game' && SPORT_IDS_WITH_POINTS.includes(sportId)
            ? 'Points'
            : shortPeriodDescription || periodDescription;

        return (
          <Fragment key={title}>
            <span className={getScoreCellClassName(false, false, title)}>{title}</span>
            <span className={getScoreCellClassName(false, false)}>
              {getDisplayScore(homeScore)}
            </span>
            <span className={getScoreCellClassName(false, false)}>
              {getDisplayScore(awayScore)}
            </span>
          </Fragment>
        );
      },
    );
  };

  const renderGeneralScore = opts => {
    const title = opts.title || (SPORT_IDS_WITH_SETS.includes(sportId) ? 'Sets' : 'Score');

    return (
      <>
        {SPORT_IDS_WITHOUT_PERIOD_VIEW.includes(sportId) ? (
          <span className="hr name">
            <Icon className="icon-score" name="score" />
          </span>
        ) : (
          <Label className={getScoreCellClassName(false, false, title)} message={title} />
        )}
        <span className={getScoreCellClassName(true, true)}>{getDisplayScore(homeScore)}</span>
        <span className={getScoreCellClassName(true, true)}>{getDisplayScore(awayScore)}</span>
      </>
    );
  };

  const renderAdditionalInfo = () => {
    const awayCopy = { ...away };
    delete awayCopy?.points;

    return Object.keys(awayCopy).map(key => {
      return (
        (key === 'corners' ||
          key === 'penalties' ||
          key === 'yellow_cards' ||
          key === 'red_cards') && (
          <Fragment key={key}>
            {key === 'corners' && (
              <span className="hr name">
                <Icon className="icon-game-stats" name="corners" />
              </span>
            )}
            {key === 'penalties' && (
              <span className="hr name">
                <Icon className="icon-game-stats" name="penalty" />
              </span>
            )}
            {key === 'yellow_cards' && (
              <span className="hr name">
                <Icon className="icon-game-stats" name="yellow-card" />
              </span>
            )}
            {key === 'red_cards' && (
              <span className="hr name">
                <Icon className="icon-game-stats" name="red-card" />
              </span>
            )}
            <span className={getScoreCellClassName()}>{home[key]}</span>
            <span className={getScoreCellClassName()}>{away[key]}</span>
          </Fragment>
        )
      );
    });
  };

  const lapsLabel = useLabel('Laps');
  const customScoreName = sportId === 23 && lapsLabel;

  const isArchived = sportService === SPORT_SERVICE.ARCHIVED;

  return (
    <div className="gameStat">
      <div className={cx('game-stat-info', { 'game-stat-info__outright': event.outright })}>
        <div className="tournament-scroll-event-info-data">
          {sportService === SPORT_SERVICE.LIVE && (
            <>
              <EventLiveStatus />
              {shownEventId && (
                <span className="sub-title-xsmall-500-text">
                  ID:
                  {event.id}
                </span>
              )}
            </>
          )}
        </div>
        {event.outright && (
          <div className="event-participants">
            <div className="event-participant">
              <div className="event-participant__name">{event.name}</div>
            </div>
          </div>
        )}
        {!event.outright && (
          <EventLiveParticipants
            hidePitch={isArchived}
            hideScore
            participants={participants}
            score={score}
          />
        )}
      </div>

      <div className="additional">
        {renderGeneralScore({ title: customScoreName })}
        {!customScoreName && renderPeriodView()}
        {renderAdditionalInfo()}
      </div>
    </div>
  );
};

Scoreboard.propTypes = { event: eventShape.isRequired };

export default Scoreboard;
